import { DialogTitle } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import Error from "components/Error";
import Button from "components/buttons/Button";
import TextAreaFormField from "components/form/TextAreaFormField";
import Modal from "components/modal/Modal";
import {
  FeedbackFormTypeEnum,
  FeedbackSubmissionStatusEnum,
  QaFormRequirementEnum,
} from "pages/project/common/taskBase.typing";
import { COMMENT_MAX_CHAR_LIMIT, useDeliverTaskForm } from "pages/project/task/components/modals/useDeliverTaskForm";
import { FeedbackFormSection } from "pages/project/task/components/modals/FeedbackFormSection";
import { useQAForm, useTask, useDeliverTaskMutation } from "query/task.query";
import { useTranslation } from "react-i18next";

const ICRTaskType = "QC 8-VALID";

interface Props {
  show: boolean;
  taskId: string;
  isUserSupplier: boolean;
}

const DeliverTaskModal = ({ show, taskId, isUserSupplier }: Props) => {
  const { t } = useTranslation("translation", {
    keyPrefix: isUserSupplier ? "task.modal.deliver" : "task.modal.validate",
  });
  const { isLoading: isTaskLoading, selectedTask } = useTask(taskId);

  // Determine which form to show based on feedbackFormType
  const feedbackFormType = selectedTask?.feedbackFormType ?? FeedbackFormTypeEnum.None;
  // Only show feedback forms if feedback hasn't been submitted yet (prevents showing form on reopened tasks)
  const feedbackNotYetSubmitted = selectedTask?.feedbackSubmissionStatus === FeedbackSubmissionStatusEnum.NotSubmitted;
  const shouldShowQAForm = selectedTask?.shouldDisplayQAForm && feedbackNotYetSubmitted;
  const shouldShowMTForm = selectedTask?.translationTaskMtEngineFormRequirement && feedbackNotYetSubmitted;

  const { isLoading: isQAFormLoading, qaForm } = useQAForm(selectedTask);
  const { deliverTask, isPending: isDelivering } = useDeliverTaskMutation(
    taskId,
    selectedTask?.catToolId,
    isUserSupplier
  );

  const { deliver, reset, register, formState, qaFormRatings, rated3StarsOrLess, setValue, validationError } =
    useDeliverTaskForm(
      taskId,
      qaForm,
      shouldShowQAForm,
      selectedTask?.catToolId,
      isUserSupplier,
      shouldShowMTForm,
      // Callback: deliver the task with form data
      ({ comment, remarks, ratings }) => {
        deliverTask(true, true, comment, remarks, ratings, false);
      },
      selectedTask?.qaFormRequirement === QaFormRequirementEnum.Optional, // Optional if qaFormRequirement === Optional
      false, // isFeedbackOnly
      selectedTask?.taskType === ICRTaskType // isStarRating
    );

  const isPending = isDelivering;

  if (isTaskLoading || !selectedTask) {
    return null;
  }
  return (
    <>
      <Modal
        isShown={show}
        onClose={reset}
        maxWidthClassName={shouldShowQAForm || shouldShowMTForm ? "sm:max-w-7xl" : undefined}
      >
        <div>
          {/*Icon*/}
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="size-6 text-green-600" aria-hidden="true" />
          </div>
          {/*Header*/}
          <div className="mt-3 text-center sm:mt-5">
            <DialogTitle as="h3" className="text-color-primary text-lg font-medium leading-6 sm:text-3xl">
              <div>{t("title")}</div>
              <div className="text-base text-gray-600 sm:text-lg">{selectedTask.taskCode}</div>
            </DialogTitle>
          </div>

          {/*body*/}
          <div>
            <form className="mx-4 mt-6 flex flex-col space-y-5" onSubmit={deliver}>
              {shouldShowQAForm || shouldShowMTForm ? (
                <>
                  <FeedbackFormSection
                    qaForm={qaForm}
                    isQAFormLoading={isQAFormLoading}
                    qaFormRatings={qaFormRatings}
                    setValue={setValue}
                    feedbackFormType={feedbackFormType}
                    shouldShowQAForm={!!shouldShowQAForm}
                    shouldShowMTForm={!!shouldShowMTForm}
                    isStarRating={selectedTask.taskType === ICRTaskType}
                    register={register}
                    formState={formState}
                    rated3StarsOrLess={rated3StarsOrLess}
                    taskType={selectedTask.taskType}
                    isUserSupplier={isUserSupplier}
                    t={t}
                  />

                  {/* General Information Section */}
                  <div className="space-y-4">
                    <div className="text-left">
                      <h4 className="text-sm font-semibold">{t("form.general-section-title")}</h4>
                      <p className="mt-1 text-sm">{t("form.general-section-description")}</p>
                    </div>

                    <TextAreaFormField
                      id="remarks"
                      label={t("form.general-remarks")}
                      className="max-h-52"
                      {...register("remarks", {
                        maxLength: {
                          value: COMMENT_MAX_CHAR_LIMIT,
                          message: t(`form.commentInvalid`, { maxCharLimit: COMMENT_MAX_CHAR_LIMIT }),
                        },
                      })}
                      error={formState.errors.remarks?.message}
                      maxCharLimit={COMMENT_MAX_CHAR_LIMIT}
                    />
                  </div>
                </>
              ) : (
                <TextAreaFormField
                  id="remarks"
                  label={t("form.comment")}
                  className="max-h-52"
                  autoFocus={true}
                  {...register("remarks", {
                    maxLength: {
                      value: COMMENT_MAX_CHAR_LIMIT,
                      message: t(`form.commentInvalid`, { maxCharLimit: COMMENT_MAX_CHAR_LIMIT }),
                    },
                  })}
                  error={formState.errors.remarks?.message}
                  maxCharLimit={COMMENT_MAX_CHAR_LIMIT}
                />
              )}
              {validationError ? <Error error={validationError} /> : null}
              <div className="ml-auto w-full max-w-3xl">
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm sm:mt-6 sm:grid-flow-row-dense">
                  <Button type="reset" disabled={isPending} color="ternary" label={t("cancel")} onClick={reset} />
                  <Button type="submit" disabled={isPending} color="primary" label={t("confirm")} />
                </div>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default DeliverTaskModal;
